import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { LanguageProvider } from "@/context/LanguageContext";
import { Toaster } from "@/components/ui/sonner";
import AppShell from "@/components/AppShell";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import SsoCallback from "@/pages/SsoCallback";
import Dashboard from "@/pages/Dashboard";
import Applications from "@/pages/Applications";
import ApplicationDetail from "@/pages/ApplicationDetail";
import MapView from "@/pages/MapView";
import Circulations from "@/pages/Circulations";
import Financials from "@/pages/Financials";
import Analytics from "@/pages/Analytics";
import Portal from "@/pages/Portal";
import PublicTour from "@/pages/PublicTour";
import PrintReport from "@/pages/PrintReport";
import DeveloperHome from "@/pages/DeveloperHome";
import DeveloperLogin from "@/pages/DeveloperLogin";
import Register from "@/pages/Register";
import Admin from "@/pages/Admin";
import Reports from "@/pages/Reports";
import FieldApp from "@/pages/FieldApp";

function HtmlRedirect({ to }) {
    window.location.replace(to);
    return null;
}

function Protected({ children }) {
    const { user, loading } = useAuth();
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="flex flex-col items-center gap-3">
                    <div className="w-10 h-10 rounded-[10px] bg-teal animate-pulse" />
                    <div className="text-sm text-slate-400 font-data">Loading LandLogic NexusOne…</div>
                </div>
            </div>
        );
    }
    if (!user) return <Navigate to="/login" replace />;
    if (user.role === "developer") return <Navigate to="/developer" replace />;
    if (user.role === "inspector") return <Navigate to="/field" replace />;
    return children;
}

function FieldProtected({ children }) {
    const { user, loading } = useAuth();
    if (loading) return null;
    if (!user) return <Navigate to="/login" replace />;
    if (user.role === "developer") return <Navigate to="/developer" replace />;
    return children;
}

function App() {
    return (
        <ThemeProvider>
            <AuthProvider>
            <LanguageProvider>
                <BrowserRouter>
                    <Routes>
                        <Route path="/" element={<Landing />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/sso/callback" element={<SsoCallback />} />
                        <Route path="/portal" element={<Portal />} />
                        <Route path="/tour" element={<PublicTour />} />
                        <Route path="/developer/login" element={<DeveloperLogin />} />
                        <Route path="/register" element={<Register />} />
                        <Route path="/developer" element={<DeveloperHome />} />
                        <Route path="/client-summary.html" element={<HtmlRedirect to="/feature-comparison.html" />} />
                        <Route path="/developer/applications" element={<DeveloperHome initialTab="mine" />} />
                        <Route path="/developer/hearings" element={<DeveloperHome initialTab="hearings" />} />
                        <Route
                            path="/applications/:id/print"
                            element={
                                <Protected>
                                    <PrintReport />
                                </Protected>
                            }
                        />
                        <Route
                            path="/field"
                            element={
                                <FieldProtected>
                                    <FieldApp />
                                </FieldProtected>
                            }
                        />
                        <Route
                            element={
                                <Protected>
                                    <AppShell />
                                </Protected>
                            }
                        >
                            <Route path="/dashboard" element={<Dashboard />} />
                            <Route path="/applications" element={<Applications />} />
                            <Route path="/applications/:id" element={<ApplicationDetail />} />
                            <Route path="/map" element={<MapView />} />
                            <Route path="/circulations" element={<Circulations />} />
                            <Route path="/financials" element={<Financials />} />
                            <Route path="/analytics" element={<Analytics />} />
                            <Route path="/reports" element={<Reports />} />
                            <Route path="/admin" element={<Admin />} />
                        </Route>
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                    <Toaster position="bottom-right" richColors />
                </BrowserRouter>
            </LanguageProvider>
            </AuthProvider>
        </ThemeProvider>
    );
}

export default App;
